import { all, delay, fork, cancel, takeLatest, takeLeading, call, put } from '@redux-saga/core/effects';
import axios from 'axios';

import { ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';
import { retrieveFromLocalStorage } from '../../utils/util';

import {
  requestCreateMessage,
  requestViewAllChats,
  requestViewChat,
  startPollingChat,
  stopPollingChat,
  requestViewSupportChat,
  requestCreateSupportMessage,
  requestViewAllSupportChats,
} from './chat.actions';
import {
  getCreateMessageRequest,
  getViewAllChatsRequest,
  getViewChatRequest,
  getViewSupportChatRequest,
  getCreateSupportMessageRequest,
  getViewAllSupportChatsRequest,
} from './chat.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setChats, setChat } from '../../slices/chat/chat.slice';
import { setSupportChat, setSupportChats } from '../../slices/help-center/help-center.slice';

export function* performCreateMessageRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(stopPollingChat());

    const [endpoint, options] = getCreateMessageRequest(input);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    const { personExternalReference, chatExternalReference } = input;
    yield put(startPollingChat({ personExternalReference, chatExternalReference, showInitialSpinner: false }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForCreateMessageRequest() {
  yield takeLatest(requestCreateMessage.type, performCreateMessageRequest);
}

export function* performViewAllChatsRequest({ payload }) {
  const { personExternalReference, showSpinner = true } = payload;

  try {
    if (showSpinner) {
      yield put(addSpinner('Retrieving Chats'));
    }
    const [endpoint, options] = getViewAllChatsRequest(personExternalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(setChats(data.chatOverviews));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    if (showSpinner) {
      yield put(removeSpinner('Retrieving Chats'));
    }
  }
}

export function* watchForViewAllChatsRequest() {
  yield takeLeading(requestViewAllChats.type, performViewAllChatsRequest);
}

export function* performViewChatRequest({ payload }) {
  const { personExternalReference, chatExternalReference, showSpinner = true } = payload;

  try {
    if (showSpinner) {
      yield put(addSpinner('Retrieving Chat'));
    }

    const [endpoint, options] = getViewChatRequest(personExternalReference, chatExternalReference);

    const { data } = yield call(axios, endpoint, options);

    delete data.message;

    yield put(setChat(data));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    if (showSpinner) {
      yield put(removeSpinner('Retrieving Chat'));
    }
  }
}

export function* watchForViewChatRequest() {
  yield takeLeading(requestViewChat.type, performViewChatRequest);
}

export function* pollChat({ payload }) {
  const { chatExternalReference, showInitialSpinner = true } = payload;
  if (chatExternalReference) {
    if (showInitialSpinner) {
      yield put(requestViewAllChats(payload));
      yield put(requestViewChat(payload));
      yield delay(5000);
    }

    while (true) {
      yield put(requestViewAllChats({ ...payload, showSpinner: false }));
      yield put(requestViewChat({ ...payload, showSpinner: false }));
      yield delay(5000);
    }
  }

  if (showInitialSpinner) {
    yield put(requestViewAllChats(payload));
    yield delay(5000);
  }

  while (true) {
    yield put(requestViewAllChats({ ...payload, showSpinner: false }));
    yield delay(5000);
  }
}

export function* performStartPollingChat({ payload }) {
  const task = yield fork(pollChat, { payload });
  yield watchForStopPollingChat(task);
}

export function* watchForStartPollingChat() {
  yield takeLatest(startPollingChat.type, performStartPollingChat);
}

export function* performStopPollingChat(task) {
  yield cancel(task);
}

export function* watchForStopPollingChat(task) {
  yield takeLatest(stopPollingChat.type, performStopPollingChat, task);
}

export function* performViewSupportChatRequest({ payload }) {
  const { showSpinner = true } = payload;

  try {
    if (showSpinner) {
      yield put(addSpinner('Retrieving Support Chat'));
    }

    const personExternalReference = retrieveFromLocalStorage('personExternalReference');
    const [endpoint, options] = getViewSupportChatRequest(personExternalReference);

    const { data } = yield call(axios, endpoint, options);

    delete data.message;

    yield put(setSupportChat(data));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    if (showSpinner) {
      yield put(removeSpinner('Retrieving Support Chat'));
    }
  }
}

export function* watchForViewSupportChatRequest() {
  yield takeLeading(requestViewSupportChat.type, performViewSupportChatRequest);
}

export function* performCreateSupportMessageRequest({ payload }) {
  const { input, admin, onSuccess, onError } = payload;

  try {
    const [endpoint, options] = getCreateSupportMessageRequest(input);

    yield call(axios, endpoint, options);

    if (admin) {
      const personExternalReference = retrieveFromLocalStorage('personExternalReference');
      yield put(
        requestViewChat({
          personExternalReference,
          chatExternalReference: input.chatExternalReference,
          showSpinner: false,
        })
      );
    } else {
      yield put(requestViewSupportChat({}));
    }

    if (onSuccess) {
      yield call(onSuccess);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));

    if (onError) {
      yield call(onError);
    }
  }
}

export function* watchForCreateSupportMessageRequest() {
  yield takeLatest(requestCreateSupportMessage.type, performCreateSupportMessageRequest);
}

export function* performViewAllSupportChatsRequest({ payload }) {
  const { showSpinner = true } = payload;

  try {
    if (showSpinner) {
      yield put(addSpinner('Retrieving Support Chats'));
    }
    const [endpoint, options] = getViewAllSupportChatsRequest();

    const { data } = yield call(axios, endpoint, options);

    yield put(setSupportChats(data.chatOverviews));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    if (showSpinner) {
      yield put(removeSpinner('Retrieving Support Chats'));
    }
  }
}

export function* watchForViewAllSupportChatsRequest() {
  yield takeLeading(requestViewAllSupportChats.type, performViewAllSupportChatsRequest);
}

export default function* chatSaga() {
  yield all([
    watchForCreateMessageRequest(),
    watchForViewAllChatsRequest(),
    watchForViewChatRequest(),
    watchForStartPollingChat(),
    watchForViewSupportChatRequest(),
    watchForCreateSupportMessageRequest(),
    watchForViewAllSupportChatsRequest(),
  ]);
}
